import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import { API_BASE_URL, FONT_UI, FONT_HEADING } from "../layouts/constants";

function getToken() {
  return (
    localStorage.getItem("guardToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token")
  );
}

function isGuardSession() {
  return localStorage.getItem("isGuardLoggedIn") === "true";
}

export default function SentinelQRCodePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [checkpoints, setCheckpoints] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("all");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const isGuardLoggedIn = localStorage.getItem("isGuardLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const guardData = (() => {
      try {
        return JSON.parse(localStorage.getItem("guardData") || "{}");
      } catch {
        return {};
      }
    })();

    if (!token || (!isLoggedIn && !isGuardLoggedIn)) {
      navigate("/");
      return;
    }

    const local = isGuardSession()
      ? {
          company_name:
            guardData.fullName ||
            guardData.name ||
            guardData.fld_name ||
            localStorage.getItem("guardName") ||
            "Guard",
          licensedFor: "Guard QR Access",
          admin_active: 1,
          company_address: "",
          company_phone: "",
          admin_email:
            guardData.email || guardData.fld_mail || localStorage.getItem("guardEmail") || "",
          company_logo: "",
          user_map: localStorage.getItem("user_map") || "",
        }
      : {
          company_name:
            localStorage.getItem("company_name") || userData.company_name || "",
          licensedFor:
            localStorage.getItem("licensedFor") || userData.licensedFor || "",
          admin_active: Number(
            localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
          ),
          company_address:
            localStorage.getItem("company_address") ||
            userData.company_address ||
            "",
          company_phone:
            localStorage.getItem("company_phone") || userData.company_phone || "",
          admin_email:
            localStorage.getItem("admin_email") || userData.admin_email || "",
          company_logo:
            localStorage.getItem("company_logo") || userData.company_logo || "",
          user_map: localStorage.getItem("user_map") || userData.user_map || "",
        };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  const normalizeQRList = (result) => {
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result)) return result;
    return [];
  };

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const guardSession = isGuardSession();

      // Guards don't have access to the admin-only company dashboard endpoint —
      // skip that call entirely for guard sessions instead of letting it
      // abort the whole page load.
      const dashboardPromise = guardSession
        ? Promise.resolve(null)
        : fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

      const [dashboardRes, checkpointRes, routeRes] = await Promise.all([
        dashboardPromise,
        fetch(`${API_BASE_URL}/qrcode/sentinel/checkpoints`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/qrcode/sentinel/routes`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const checkpointResult = await checkpointRes.json();
      const routeResult = await routeRes.json();

      if (!checkpointRes.ok || checkpointResult.success === false) {
        throw new Error(
          checkpointResult.message || "Failed to fetch sentinel checkpoint QR"
        );
      }

      if (!routeRes.ok || routeResult.success === false) {
        throw new Error(
          routeResult.message || "Failed to fetch sentinel route QR"
        );
      }

      if (!guardSession && dashboardRes) {
        const dashboardResult = await dashboardRes.json();

        if (!dashboardRes.ok || dashboardResult.success === false) {
          throw new Error(dashboardResult.message || "Failed to fetch dashboard");
        }

        const companyDetails = dashboardResult?.data?.companyDetails || {};

        setDashboardData({
          ...local,
          company_name: companyDetails.company_name ?? local.company_name ?? "",
          licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
          admin_active: Number(
            companyDetails.admin_active ?? local.admin_active ?? 0
          ),
          company_address:
            companyDetails.company_address ?? local.company_address ?? "",
          company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
          admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
          company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
          user_map: companyDetails.user_map ?? local.user_map ?? "",
        });
      }

      setCheckpoints(normalizeQRList(checkpointResult));
      setRoutes(normalizeQRList(routeResult));
    } catch (error) {
      console.error("Sentinel QR page error:", error);
      setErrorMessage(error.message || "Failed to load sentinel QR codes.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google" ? "/route-master" : "/route-master1",
    [dashboardData.user_map]
  );

  const stats = useMemo(
    () => ({
      checkpointCount: checkpoints.length,
      routeCount: routes.length,
      totalCount: checkpoints.length + routes.length,
    }),
    [checkpoints, routes]
  );

  // Build unique route options from both routes and checkpoints
  const routeOptions = useMemo(() => {
    const map = new Map();
    routes.forEach((item) => {
      const data = item.qr_data || {};
      const id = String(data.route_id || item.reference_id || item.id || "");
      const name = data.route_name || item.reference_name || id;
      if (id && !map.has(id)) map.set(id, name);
    });
    checkpoints.forEach((item) => {
      const data = item.qr_data || {};
      const id = String(data.route_id || "");
      const name = data.route_name || id;
      if (id && !map.has(id)) map.set(id, name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [routes, checkpoints]);

  // Apply text search
  const textFilteredCheckpoints = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return checkpoints;
    return checkpoints.filter((item) => {
      const data = item.qr_data || {};
      return (
        String(item.reference_id || "").toLowerCase().includes(keyword) ||
        String(item.reference_name || "").toLowerCase().includes(keyword) ||
        String(item.qr_type || "").toLowerCase().includes(keyword) ||
        String(data.route_id || "").toLowerCase().includes(keyword) ||
        String(data.route_name || "").toLowerCase().includes(keyword) ||
        String(data.checkpoint_id || "").toLowerCase().includes(keyword) ||
        String(data.checkpoint_name || "").toLowerCase().includes(keyword)
      );
    });
  }, [checkpoints, search]);

  const textFilteredRoutes = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return routes;
    return routes.filter((item) => {
      const data = item.qr_data || {};
      return (
        String(item.reference_id || "").toLowerCase().includes(keyword) ||
        String(item.reference_name || "").toLowerCase().includes(keyword) ||
        String(item.qr_type || "").toLowerCase().includes(keyword) ||
        String(data.route_id || "").toLowerCase().includes(keyword) ||
        String(data.route_name || "").toLowerCase().includes(keyword) ||
        String(data.from || "").toLowerCase().includes(keyword) ||
        String(data.to || "").toLowerCase().includes(keyword)
      );
    });
  }, [routes, search]);

  // Build grouped view: [ { routeId, routeName, routeQR, checkpointQRs } ]
  const groupedByRoute = useMemo(() => {
    const groups = new Map();

    // Seed groups from route QRs
    textFilteredRoutes.forEach((item) => {
      const data = item.qr_data || {};
      const routeId = String(data.route_id || item.reference_id || item.id || "unknown");
      const routeName = data.route_name || item.reference_name || routeId;
      if (!groups.has(routeId)) {
        groups.set(routeId, { routeId, routeName, routeQR: null, checkpointQRs: [] });
      }
      groups.get(routeId).routeQR = item;
    });

    // Add checkpoint QRs into their route group
    textFilteredCheckpoints.forEach((item) => {
      const data = item.qr_data || {};
      const routeId = String(data.route_id || "unknown");
      const routeName = data.route_name || routeId;
      if (!groups.has(routeId)) {
        groups.set(routeId, { routeId, routeName, routeQR: null, checkpointQRs: [] });
      }
      groups.get(routeId).checkpointQRs.push(item);
    });

    // Sort groups by routeName
    return Array.from(groups.values()).sort((a, b) =>
      a.routeName.localeCompare(b.routeName)
    );
  }, [textFilteredRoutes, textFilteredCheckpoints]);

  // Apply route dropdown filter on top of grouped data
  const visibleGroups = useMemo(() => {
    if (selectedRoute === "all") return groupedByRoute;
    return groupedByRoute.filter((g) => g.routeId === selectedRoute);
  }, [groupedByRoute, selectedRoute]);

  const downloadQR = async (imagePath, fileName) => {
    try {
      if (!imagePath) return;

      const token = getToken();

      const response = await fetch(
        `${API_BASE_URL}/qrcode/download?path=${encodeURIComponent(imagePath)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download QR image");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${fileName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("QR download error:", error);
      setErrorMessage("Failed to download QR image.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading sentinel QR codes...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-qr-code"
      activeMenu="sentinel-qr-code"
    >
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel QR Code
        </h2>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          View and download sentinel checkpoint and route QR images
        </p>
      </div>

      {errorMessage && <Alert type="error" message={errorMessage} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <StatCard title="Checkpoint QR Codes" value={stats.checkpointCount} color="blue" />
        <StatCard title="Route QR Codes" value={stats.routeCount} color="green" />
        <StatCard title="Total QR Codes" value={stats.totalCount} color="slate" />
      </div>

      {/* Search + Filter Panel */}
      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
        <div className="flex justify-between items-center gap-4 flex-wrap mb-5">
          <div>
            <h3
              className="m-0 text-[22px] font-normal text-slate-900"
              style={{ fontFamily: FONT_HEADING }}
            >
              Sentinel QR Management
            </h3>
            <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
              Filter by route, then search within results
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              const token = getToken();
              fetchPageData(dashboardData, token);
            }}
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            style={{ fontFamily: FONT_UI }}
          >
            Refresh
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Route dropdown */}
          <select
            value={selectedRoute}
            onChange={(e) => setSelectedRoute(e.target.value)}
            className="sm:w-64 px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            style={{ fontFamily: FONT_UI }}
          >
            <option value="all">All Routes</option>
            {routeOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>

          {/* Text search */}
          <input
            type="text"
            placeholder="Search by QR type, route, checkpoint, reference name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            style={{ fontFamily: FONT_UI }}
          />
        </div>
      </div>

      {/* Grouped Route Sections */}
      {visibleGroups.length === 0 ? (
        <div
          className="p-8 text-center text-slate-500 bg-white rounded-2xl border border-slate-100"
          style={{ fontFamily: FONT_UI }}
        >
          No QR codes found for the selected filter.
        </div>
      ) : (
        visibleGroups.map((group) => (
          <RouteGroup
            key={group.routeId}
            group={group}
            onDownload={downloadQR}
            variant="sentinel"
          />
        ))
      )}
    </Layout>
  );
}

// ─── Route Group Section ──────────────────────────────────────────────────────
function RouteGroup({ group, onDownload, variant = "patrol" }) {
  const { routeName, routeQR, checkpointQRs } = group;

  return (
    <div className="mb-8">
      {/* Route group header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-slate-200" />
        <span
          className="px-4 py-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold whitespace-nowrap"
          style={{ fontFamily: FONT_UI }}
        >
          {routeName}
        </span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      {/* Route QR first */}
      {routeQR && (
        <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-4">
          <h3
            className="m-0 mb-5 text-[18px] font-semibold text-green-700"
            style={{ fontFamily: FONT_HEADING }}
          >
            Route QR
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            <QRCard item={routeQR} type="route" onDownload={onDownload} variant={variant} />
          </div>
        </div>
      )}

      {/* Checkpoint QRs after */}
      {checkpointQRs.length > 0 && (
        <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100">
          <h3
            className="m-0 mb-5 text-[18px] font-semibold text-blue-700"
            style={{ fontFamily: FONT_HEADING }}
          >
            Checkpoint QRs
            <span
              className="ml-2 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold"
              style={{ fontFamily: FONT_UI }}
            >
              {checkpointQRs.length}
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {checkpointQRs.map((item, index) => (
              <QRCard
                key={`cp-${item.id || index}`}
                item={item}
                type="checkpoint"
                onDownload={onDownload}
                variant={variant}
              />
            ))}
          </div>
        </div>
      )}

      {!routeQR && checkpointQRs.length === 0 && (
        <div
          className="p-6 text-center text-slate-400 bg-white rounded-2xl border border-slate-100"
          style={{ fontFamily: FONT_UI }}
        >
          No QR codes available for this route.
        </div>
      )}
    </div>
  );
}

// ─── Single QR Card ───────────────────────────────────────────────────────────
function QRCard({ item, type, onDownload, variant = "patrol" }) {
  const data = item.qr_data || {};

  const id =
    type === "checkpoint"
      ? data.checkpoint_id || item.reference_id || item.id
      : data.route_id || item.reference_id || item.id;

  const name =
    type === "checkpoint"
      ? data.checkpoint_name || item.reference_name || "-"
      : data.route_name || item.reference_name || "-";

  const routeId = data.route_id || item.reference_id || "-";
  const routeName = data.route_name || "-";
  const checkpointId = data.checkpoint_id || item.reference_id || "-";
  const checkpointName = data.checkpoint_name || item.reference_name || "-";
  const lat = data.lat ?? data.latitude ?? "-";
  const lng = data.lng ?? data.longitude ?? "-";

  // Sentinel-specific route fields
  const from = data.from || "-";
  const to = data.to || "-";
  const fromLat = data.from_lat ?? "-";
  const fromLng = data.from_lng ?? "-";
  const toLat = data.to_lat ?? "-";
  const toLng = data.to_lng ?? "-";
  const clientId = data.client_id ?? "-";

  const qrImage = item.qr_image_url || item.qr_code_id || "";
  const downloadPath = item.qr_code_id || "";

  const prefix = variant === "sentinel" ? "sentinel" : "patrol";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:bg-slate-50 transition">
      <div className="flex justify-between gap-3 mb-4">
        <div>
          <h4
            className="m-0 text-lg font-semibold text-slate-900"
            style={{ fontFamily: FONT_UI }}
          >
            {name}
          </h4>
          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            {type === "checkpoint"
              ? `${variant === "sentinel" ? "Sentinel" : "Patrol"} Checkpoint QR`
              : `${variant === "sentinel" ? "Sentinel" : "Patrol"} Route QR`}
          </p>
        </div>

        <span
          className={`h-fit px-3 py-1.5 rounded-full text-xs font-semibold ${
            type === "checkpoint"
              ? "bg-blue-50 text-blue-700"
              : "bg-green-50 text-green-700"
          }`}
          style={{ fontFamily: FONT_UI }}
        >
          {item.qr_type || type}
        </span>
      </div>

      <div className="flex justify-center bg-slate-50 rounded-2xl p-4 border border-slate-100 mb-4">
        {qrImage ? (
          <img
            src={qrImage}
            alt={name}
            className="w-[190px] h-[190px] object-contain"
          />
        ) : (
          <div
            className="w-[190px] h-[190px] flex items-center justify-center text-slate-400 text-sm"
            style={{ fontFamily: FONT_UI }}
          >
            QR image not found
          </div>
        )}
      </div>

      <div className="space-y-1 text-sm text-slate-600" style={{ fontFamily: FONT_UI }}>
        <p><b>Record ID:</b> {item.id || "-"}</p>
        <p><b>Reference ID:</b> {item.reference_id || "-"}</p>
        <p><b>QR Type:</b> {item.qr_type || "-"}</p>

        {type === "checkpoint" ? (
          <>
            <p><b>Route ID:</b> {routeId}</p>
            <p><b>Route Name:</b> {routeName}</p>
            <p><b>Checkpoint ID:</b> {checkpointId}</p>
            <p><b>Checkpoint:</b> {checkpointName}</p>
            <p><b>Lat / Lng:</b> {lat}, {lng}</p>
          </>
        ) : variant === "sentinel" ? (
          <>
            <p><b>Route ID:</b> {routeId}</p>
            <p><b>Route Name:</b> {routeName}</p>
            <p><b>Client ID:</b> {clientId}</p>
            <p><b>From:</b> {from}</p>
            <p><b>From Lat/Lng:</b> {fromLat}, {fromLng}</p>
            <p><b>To:</b> {to}</p>
            <p><b>To Lat/Lng:</b> {toLat}, {toLng}</p>
          </>
        ) : (
          <>
            <p><b>Route ID:</b> {routeId}</p>
            <p><b>Route Name:</b> {routeName}</p>
          </>
        )}
      </div>

      <button
        type="button"
        disabled={!downloadPath}
        onClick={() =>
          onDownload(downloadPath, `${prefix}_${type}_${id || "item"}`)
        }
        className="mt-4 w-full px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ fontFamily: FONT_UI }}
      >
        Download QR
      </button>
    </div>
  );
}

// ─── Shared UI Components ─────────────────────────────────────────────────────
function StatCard({ title, value, color }) {
  const styles = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    green: "bg-green-50 text-green-700 border-green-100",
    slate: "bg-slate-50 text-slate-900 border-slate-200",
  };

  return (
    <div className={`rounded-2xl border px-6 py-5 ${styles[color]}`}>
      <p className="text-sm font-semibold mb-2" style={{ fontFamily: FONT_UI }}>
        {title}
      </p>
      <h3 className="m-0 text-4xl font-semibold" style={{ fontFamily: FONT_HEADING }}>
        {value}
      </h3>
    </div>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";
  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}